<!--
 * @Author: Ygm
 * @Date: 2024-08-07 14:59:26
 * @LastEditors: Ygm
 * @LastEditTime: 2025-07-10 16:15:02
 * @Description: 基础计划选择
 -->
<script>
import { planStatusEnum } from '@/view/pages/Enum'
import { readSion } from '@/view/public/common'
import { getWorkgroupList } from '@/apis/inputSpace/infoCategory'
import { request } from '@/utils/request'

export default {
  name: 'BasePlan',
  data() {
    return {
      labelCol: { span: 1, offset: 0 },
      wrapperCol: { span: 8 },
      planList: [],
      basePlan: {
        planId: []
      }
    }
  },

  filters: {
    // 处理选择的项目计划名
    handelPlanName(value) {
      if (!value || !value.length) return ''

      const len = value.length - 1
      const newPlanName = value[len].replace(planStatusEnum['0'], '')
      return value.length === 1 ? value[0] : value[0] + ' - ' + newPlanName
    }
  },

  created() {
    this.getProjectPlan()
  },

  methods: {
    // 获取项目计划列表
    async getProjectPlan() {
      this.$setLoading({ tip: '' })
      const { spyId } = this.$store.state
      const url = '/api/orgproject/project/myAllProject'
      await request(url, { spyId })
        .then(async res => {
          const { data } = res
          if (data.result) {
            this.planList = this.handlePlanList(data.data)
            this.$store.commit('setPlanOptions', data.data)

            this.setDefaultPlan()
            await this.getWorkgroupListByOwn()
            this.$emit('change')
          } else {
            this.$setLoading(false)
            this.$message.warn(data.message)
          }
        })
        .catch(err => {
          this.$setLoading(false)
          this.$message.warn(err.message)
        })
    },

    // 设置默认计划选择的计划
    setDefaultPlan() {
      const { planId, projectId } = this.$store.state.projectPlan
      let newProjectId = ''
      let newPlanId = ''
      const planExistsInList = this.isIdInTree(this.planList, planId)

      // 如果 store 中已经有了默认的planId和projectId，并且在项目计划列表中存在
      if (planExistsInList) {
        newProjectId = projectId
        newPlanId = planId
      } else {
        const { id, projectId } = this.getLatestChildFromLatestParent(this.planList) || {}
        newProjectId = projectId
        newPlanId = id
      }

      this.basePlan.planId = newPlanId ? [newProjectId, newPlanId] : []
      const storeProPlan = { projectId: newProjectId, planId: newPlanId }
      this.$store.commit('setProjectPlan', storeProPlan)
    },

    // 判断id是否在树形数据中存在
    isIdInTree(treeData, id) {
      if (!id) return false

      for (const item of treeData) {
        if (item.id === id) {
          return true
        }
        if (item.children && item.children.length > 0) {
          if (this.isIdInTree(item.children, id)) {
            return true
          }
        }
      }
      return false
    },

    /**
     * 根据父级节点的 createTime 时间顺序，获取最近的父级节点，
     * 然后从该父级节点的 children 中返回 createTime 最近的一条子集数据。
     * @param {Array} dataArray - 树形数组
     * @returns {Object|null} - 返回最近的子集数据或 null
     */
    getLatestChildFromLatestParent(dataArray) {
      if (!dataArray || dataArray.length === 0) {
        return null // 如果数据数组为空，返回 null
      }

      // 找到 createTime 最近的父级节点
      const latestParent = dataArray.reduce((latest, current) => {
        return new Date(current.createTime) > new Date(latest.createTime) ? current : latest
      }, dataArray[0])

      if (!latestParent.children || latestParent.children.length === 0) {
        return null // 如果没有 children，返回 null
      }

      // 从最近的父级节点中找到 createTime 最近的子集数据
      return latestParent.children.reduce((latest, current) => {
        return new Date(current.createTime) > new Date(latest.createTime) ? current : latest
      }, latestParent.children[0])
    },

    // 获取项目计划下当前用户所属权限的工作组集合
    async getWorkgroupListByOwn() {
      try {
        this.$setLoading({ tip: '' })
        const { userId } = readSion('userInfo') || {}
        const { spyId, projectPlan } = this.$store.state
        const params = {
          current: 1,
          pageSize: 9999,
          uesrId: userId,
          planId: projectPlan.planId,
          spyRightCode: spyId
        }
        const res = await getWorkgroupList(params)
        if (res.result) {
          this.$store.commit('setOwnWorkgroupList', res.data || [])
        } else {
          this.$message.warn(res.message || '获取工作组列表失败')
        }
      } catch (e) {
        console.error('e', e)
      } finally {
        this.$setLoading(false)
      }
    },

    // 计划选择变化
    async onChange(value) {
      const projectPlan = {
        projectId: value[0],
        planId: value[1]
      }
      this.$store.commit('setProjectPlan', projectPlan)
      await this.getWorkgroupListByOwn()
      this.$emit('change', value)
    },

    // 处理项目计划数据
    handlePlanList(arr = []) {
      if (!arr || arr.length === 0) return []

      try {
        return arr
          .map(v => {
            // 确保必要属性存在
            if (!v.id || !v.name || !v.shortName) {
              throw new Error(`缺失必要属性: ${JSON.stringify(v)}`)
            }

            v.value = v.id
            v.projectName = v.name
            v.name = v.shortName

            // 子元素处理前检查children是否存在
            if (v.children && Array.isArray(v.children)) {
              v.children = v.children
                .map(c => {
                  // 确保必要属性存在以及planStatusEnum中的值有效
                  if (!c.id || !c.isEnable || !planStatusEnum[c.isEnable]) {
                    throw new Error(`缺失必要属性: ${JSON.stringify(c)}`)
                  }

                  const statusName = planStatusEnum[c.isEnable]
                  const isEnabled = c.isEnable === '1'
                  const planName = c.planShortName || c.examPlanName

                  // 通过返回对象实现状态修改，避免直接修改组件状态
                  return {
                    ...c,
                    value: c.id,
                    name: planName,
                    // 以下属性用于外部处理，避免直接修改组件状态
                    isEnabledState: true,
                    isEnabledPlan: true
                  }
                })
                .filter(v => v.isEnabledState)
            } else {
              delete v.children
            }

            return v
          })
          .filter(o => o.children && o.children.length > 0)
      } catch (error) {
        console.log('Failed', error)
        return []
      }
    }
  }
}
</script>

<template>
  <div class="base-plan">
    <a-form-model :model="basePlan" :label-col="labelCol" :wrapper-col="wrapperCol">
      <a-form-model-item label="项目计划" style="margin-bottom: 0" class="search-plan">
        <a-cascader
          :field-names="{
            label: 'name',
            value: 'value',
            children: 'children'
          }"
          class="cascader-plan"
          expand-trigger="hover"
          placeholder="请选择项目计划"
          :allowClear="false"
          :show-search="false"
          v-model="basePlan.planId"
          :options="planList"
          @change="onChange"
        >
          <template v-slot:displayRender="{ labels }">
            {{ labels | handelPlanName }}
          </template>
        </a-cascader>
      </a-form-model-item>
    </a-form-model>
  </div>
</template>

<style scoped lang="scss">
.base-plan {
  padding: 5px 16px;
  background: #f8f9fa;
  border-radius: 4px;
  box-sizing: border-box;

  .cascader-plan {
    min-width: 300px;
  }

  /deep/ .ant-col-1 {
    width: fit-content;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/BasePlan/index.vue